import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatMoney, type PublicInvoiceOutput } from '@1financial/shared';
import { fmtDate } from '../lib/date.js';
import { DocumentBranding, PaymentInstructions } from '../components/DocumentBranding.js';

const STATUS_PILL: Record<string, string> = {
  DRAFT: 'pill-neutral',
  ISSUED: 'pill-info',
  SENT: 'pill-indigo',
  PARTIALLY_PAID: 'pill-warn',
  PAID: 'pill-success',
  OVERDUE: 'pill-danger',
  CANCELLED: 'pill-neutral',
  VOID: 'pill-neutral',
};


/**
 * Client-facing invoice view (/pay/<token>) — view-only, no payment
 * collection. Always rendered in the light theme (print convention), outside
 * the staff Shell.
 */
export function PublicInvoicePage() {
  const { token } = useParams<{ token: string }>();

  // Force light theme for this client-facing page; restore on leave.
  useEffect(() => {
    const prev = document.documentElement.dataset.theme;
    document.documentElement.dataset.theme = 'light';
    return () => {
      if (prev) document.documentElement.dataset.theme = prev;
      else delete document.documentElement.dataset.theme;
    };
  }, []);

  const detail = useQuery<PublicInvoiceOutput>({
    queryKey: ['public-invoice', token],
    queryFn: async () => {
      const res = await fetch(`/v1/public/invoice/${token}`);
      if (!res.ok) throw new Error(res.status === 404 ? 'This invoice link is not valid.' : `Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: PublicInvoiceOutput };
      return body.data;
    },
    retry: false,
  });

  if (detail.isLoading) {
    return <div className="min-h-screen bg-bg-base flex items-center justify-center text-sm text-muted">Loading invoice…</div>;
  }
  if (detail.error || !detail.data) {
    return (
      <div className="min-h-screen bg-bg-base flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-sm text-rose-400">{detail.error?.message ?? 'Invoice not found.'}</div>
        </div>
      </div>
    );
  }

  const inv = detail.data;
  const balance = { amount: inv.balanceDue, currency: inv.total.currency };

  return (
    <div className="min-h-screen bg-bg-base py-6 sm:py-10 px-4">
      <div className="w-full max-w-3xl mx-auto space-y-4">
        <DocumentBranding branding={inv.branding} />

        <div className="bg-bg-panel border border-border rounded-xl shadow-panel overflow-hidden">
          {/* Header */}
          <div className="p-6 sm:p-8 border-b border-border">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div>
                <div className="text-xs text-muted uppercase tracking-wide">Invoice</div>
                <h1 className="text-2xl font-semibold text-white mt-1">{inv.invoiceNumber}</h1>
                <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs border ${STATUS_PILL[inv.status] ?? 'pill-neutral'}`}>
                  {inv.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="sm:text-right space-y-3">
                <div>
                  <div className="text-xs text-muted">Issue date</div>
                  <div className="text-sm text-white">{fmtDate(inv.issueDate)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted">Due date</div>
                  <div className="text-sm text-white">{fmtDate(inv.dueDate)}</div>
                </div>
                {inv.paidDate && (
                  <div>
                    <div className="text-xs text-muted">Paid date</div>
                    <div className="text-sm text-emerald-300">{fmtDate(inv.paidDate)}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-muted uppercase tracking-wide">Billed to</div>
                <div className="text-sm text-white font-medium mt-1 break-words">{inv.clientName ?? '—'}</div>
                {inv.engagementTitle && <div className="text-xs text-muted mt-0.5 break-words">{inv.engagementTitle}</div>}
              </div>
              <div className="sm:text-right">
                <div className="text-xs text-muted uppercase tracking-wide">Amount due</div>
                <div className={`text-2xl font-semibold mt-1 ${inv.balanceDue > 0 ? 'text-accent' : 'text-muted'}`}>
                  {formatMoney(balance)}
                </div>
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="bg-bg-card border-b border-border text-left text-xs text-muted">
                  <th className="px-6 sm:px-8 py-3 font-medium">Description</th>
                  <th className="px-6 sm:px-8 py-3 font-medium text-right">Amount</th>
                  <th className="px-4 py-3 font-medium text-right">VAT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {inv.lineItems.map((li, i) => (
                  <tr key={i}>
                    <td className="px-6 sm:px-8 py-3 text-white break-words">{li.description}</td>
                    <td className="px-6 sm:px-8 py-3 text-right text-white">{formatMoney(li.amount)}</td>
                    <td className="px-4 py-3 text-right text-muted">{li.vatRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="p-6 sm:p-8 border-t border-border">
            <div className="sm:ml-auto sm:max-w-xs space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted">Subtotal</span>
                <span className="text-white">{formatMoney(inv.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted">VAT</span>
                <span className="text-white">{formatMoney(inv.tax)}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-white">Total</span>
                <span className="text-white">{formatMoney(inv.total)}</span>
              </div>
              {inv.payments.length > 0 && (
                <>
                  <div className="text-xs text-muted uppercase tracking-wide pt-3">Payments received</div>
                  {inv.payments.map((p, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-muted">{fmtDate(p.paidAt)}{p.method ? ` · ${p.method}` : ''}</span>
                      <span className="text-emerald-300">− {formatMoney(p.amount)}</span>
                    </div>
                  ))}
                </>
              )}
              <div className="flex justify-between text-sm font-semibold border-t border-border pt-2 mt-2">
                <span className="text-white">{inv.balanceDue > 0 ? 'Balance due' : 'Balance'}</span>
                <span className={inv.balanceDue > 0 ? 'text-accent' : 'text-muted'}>{formatMoney(balance)}</span>
              </div>
            </div>

            {inv.notes && (
              <div className="mt-6 border-t border-border pt-4">
                <div className="text-xs text-muted uppercase tracking-wide">Notes</div>
                <p className="text-sm text-white mt-1 whitespace-pre-wrap break-words">{inv.notes}</p>
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-3">
              <a
                href={`/v1/public/invoice/${token}/pdf`}
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm bg-accent hover:bg-emerald-400 text-accent-fg font-medium min-h-[44px]"
              >
                Download PDF
              </a>
            </div>
          </div>
        </div>

        <PaymentInstructions instructions={inv.paymentInstructions ?? []} />
        <p className="text-center text-xs text-muted whitespace-pre-wrap break-words">{inv.branding?.footerNote}</p>
      </div>
    </div>
  );
}
